import React from 'react';
import styled from 'styled-components';
import Link from '../components/Link';
import { FormattedMessage, injectIntl } from 'react-intl';

import BecomeExhibitorButton from '../components/BecomeExhibitorButton';
import Sponsor from '../components/Sponsor';
import Banners from '../components/Banners';
import { breakpoints } from '../constants';

import aboutBackground from './assets/about-background.png';
import exhibitorsBackground from './assets/exhibitors-background-colored.jpg';
import exhibitorsExhibitorsMobileBackground from './assets/exhibitors-background-colored-exhibitors-mob.jpg';
import aircrafts from './assets/aircrafts.png';
import visitors from './assets/visitors.png';
import conference from './assets/conference.png';

const About = styled.section`
    background: url(${aboutBackground}) center top no-repeat no-repeat;
    min-height: 557px;
    padding: 90px 200px;
    p {
        text-align: justified;
    }
    h1,
    h2 {
        color: #003056;
        font-size: 2rem;
        font-weight: 600;
        line-height: 1.5;
        word-break: break-word;
        margin-bottom: 1.5rem;
    }

    @media (max-width: ${breakpoints.tablet}) {
        background: none;
        padding: 2rem 50px 3rem;
    }

    @media (max-width: ${breakpoints.mobile}) {
        padding: 1rem 20px 2rem;
        text-align: justify;
        h1,
        h2 {
            font-size: 1.3rem;
            text-align: left;
        }
    }
`;

const Exhibitors = styled.section`
    background: url(${exhibitorsBackground}) center bottom no-repeat no-repeat;
    height: 470px;
    text-align: justify;
    p {
        margin-bottom: 1rem;
    }
    h2,
    h3,
    h4 {
        font-size: 1.5rem;
        line-height: 1.5;
        font-weight: 600;
        margin-bottom: 1.5rem;
    }
    @media (max-width: ${breakpoints.tablet}) {
        height: auto;
    }

    .column:first-of-type {
        // Exhibitors
        padding-left: 200px;
        padding-bottom: 30px;
        display: flex;
        flex-direction: column;
        a {
            color: white;
            &.button {
                align-self: flex-start;
                color: #003056;
            }
        }

        @media (max-width: ${breakpoints.tablet}) {
            padding: 2rem 50px 3rem;
            background: url(${exhibitorsExhibitorsMobileBackground}) center
                bottom no-repeat no-repeat;
            background-size: cover;
        }
        @media (max-width: ${breakpoints.mobile}) {
            padding: 1rem 30px;
        }
    }
    .column:nth-of-type(2) {
        padding-left: 35px;
        padding-right: 45px;
        padding-bottom: 30px;
        display: flex;
        flex-direction: column;
        a {
            color: white;
            &.button {
                align-self: flex-start;
                color: #003056;
            }
        }

        @media (max-width: ${breakpoints.tablet}) {
            padding: 2rem 50px 3rem;
            background-color: #003056;
        }
        @media (max-width: ${breakpoints.mobile}) {
            padding: 1rem 30px;
        }
    }
    .column:nth-of-type(3) {
        display: flex;
        align-items: center;
        justify-content: center;
        padding-right: 200px;

        @media (max-width: ${breakpoints.tablet}) {
            padding: 0 50px 0;
            background-color: #bdcdd2;
        }
        @media (max-width: ${breakpoints.mobile}) {
            padding: 0 30px;
        }
    }
    p,
    h3,
    h4 {
        color: white;
    }
    h4 {
        margin-top: 20px;
    }
`;

const Sponsors = styled.section`
    //height: 570px;
    //height: 100px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    padding: 20px 200px 40px;
    h3 {
        flex: 1 0 100%;
        text-align: center;
        color: #003056;
        font-weight: bold;
        border-bottom: 1px solid #20bbef;
        line-height: 0;
        margin-top: 50px;
        margin-bottom: 1.5rem;
        span {
            background: #fff;
            padding: 0 10px;
        }
    }
    .break {
        flex: 1 0 100%;
    }

    @media (max-width: ${breakpoints.tablet}) {
        padding: 30px;
        h3 {
            font-size: 150%;
        }
    }

    @media (max-width: ${breakpoints.mobile}) {
        h3 {
            line-height: initial;
            border: none;
            position: relative;
        }
    }
`;

const AboutItems = styled.div`
    @media (max-width: ${breakpoints.tablet}) {
        margin: 2rem 0;
    }
`;

const AboutItem = styled.div`
    p {
        line-height: 1.5;
        max-width: 120px;
        margin: 0 auto;
    }
    a {
        color: inherit;
    }
`;

const Content = styled.div`
    flex: 1;
`;

const IndexContent = ({ intl, data }) => {
    let aboutBlock = data.contentfulPage.blocks.filter(
        (block) => block.name === 'about'
    );
    aboutBlock = !!aboutBlock.length && aboutBlock[0];
    let attendeeBlock = data.contentfulPage.blocks.filter(
        (block) => block.name === 'attendee'
    );
    attendeeBlock = !!attendeeBlock.length && attendeeBlock[0];
    let exhibitorsBlock = data.contentfulPage.blocks.filter(
        (block) => block.name === 'exhibitors'
    );
    exhibitorsBlock = !!exhibitorsBlock.length && exhibitorsBlock[0];
    const sponsors = data.sponsors.edges.filter(
        (edge) => edge.node.type === 'sponsors'
    )[0].node.items;
    const infoSponsors = data.sponsors.edges.filter(
        (edge) => edge.node.type === 'info-sponsors'
    )[0].node.items;
    const generalSponsors = data.sponsors.edges.filter(
        (edge) => edge.node.type === 'general-sponsors'
    )[0].node.items;

    const { banners } = data;

    return (
        <Content>
            <About>
                <div className="columns">
                    <div className="column is-three-fifths">
                        <div
                            dangerouslySetInnerHTML={{
                                __html: aboutBlock
                                    ? aboutBlock.content.childMarkdownRemark
                                          .html
                                    : '<p/>',
                            }}
                        />
                    </div>
                    <AboutItems className="column">
                        <div className="columns is-mobile">
                            <AboutItem className="column has-text-centered">
                                <img src={visitors} />
                                <p>
                                    <FormattedMessage id="about.visitors" />
                                </p>
                            </AboutItem>
                            <AboutItem className="column has-text-centered">
                                <img src={aircrafts} />
                                <p>
                                    <FormattedMessage id="about.aircrafts" />
                                </p>
                            </AboutItem>
                        </div>
                        <div className="columns is-mobile">
                            <AboutItem className="column has-text-centered">
                                <img src={conference} />
                                <p>
                                    <Link to="http://static.rubae.ru/RUBAE_conference_program.pdf">
                                        <FormattedMessage id="about.conference" />
                                    </Link>
                                </p>
                            </AboutItem>
                            <AboutItem className="column has-text-centered">
                                <img
                                    src={conference}
                                    style={{ visibility: 'hidden' }}
                                />
                                <p>
                                    <Link to="pages/about">
                                        <FormattedMessage id="about.about" />
                                    </Link>
                                </p>
                            </AboutItem>
                        </div>
                    </AboutItems>
                </div>
            </About>
            <Exhibitors className="columns">
                <div className="column is-4">
                    <div
                        style={{ marginBottom: '20px', flex: 1 }}
                        dangerouslySetInnerHTML={{
                            __html: exhibitorsBlock
                                ? exhibitorsBlock.content.childMarkdownRemark
                                      .html
                                : '<p/>',
                        }}
                    />
                    {/* <BecomeExhibitorButton
                        label={intl.formatMessage({
                            id: 'exhibitors.exhibitors.buttonTitle',
                        })}
                    />
                    <a
                        href="https://lk.rubae.ru"
                        className="button"
                        style={{ marginTop: '0.7rem' }}
                    >
                        <FormattedMessage id="exhibitors.exhibitors.personalAccountButton" />
                    </a> */}
                </div>

                <div className="column is-3">
                    <div
                        style={{ marginBottom: '10px', flex: '1 1 0%' }}
                        dangerouslySetInnerHTML={{
                            __html: attendeeBlock
                                ? attendeeBlock.content.childMarkdownRemark.html
                                : '<p/>',
                        }}
                    />
                    {/* <Link className="button" to="pages/registration/">
                        <FormattedMessage id="exhibitors.attendee.buttonTitle" />
                    </Link> */}
                </div>
                <div className="column">
                    {banners && (
                        <Banners
                            items={banners.edges.map(({ node }) => node)}
                        />
                    )}
                </div>
            </Exhibitors>
            <Sponsors>
                <h3>
                    <FormattedMessage id="sponsors" tagName="span" />
                </h3>
                {renderSponsors(sponsors, { maxHeight: '120px' })}
                <div className="break" />
                {renderSponsors(generalSponsors, {
                    showTitle: true,
                    maxHeight: '120px',
                })}
                <h3>
                    <FormattedMessage id="info-sponsors" tagName="span" />
                </h3>
                {renderSponsors(infoSponsors, { maxHeight: '40px' })}
            </Sponsors>
        </Content>
    );
};

const renderSponsors = (
    sponsors,
    options = { maxHeight: 0, showTitle: false }
) =>
    sponsors.map((sponsor) => (
        <Sponsor
            key={sponsor.title}
            url={sponsor.link}
            img={sponsor.logo.file.url}
            title={options.showTitle && sponsor.title}
            maxHeight={options.maxHeight}
        />
    ));

export default injectIntl(IndexContent);

export const pageQuery = graphql`
    query indexTemplateQuery($id: String!, $node_locale: String!) {
        contentfulPage(id: { eq: $id }) {
            blocks {
                name
                content {
                    childMarkdownRemark {
                        html
                    }
                }
            }
        }

        sponsors: allContentfulSponsors(
            filter: { node_locale: { eq: $node_locale } }
        ) {
            edges {
                node {
                    type
                    items {
                        title
                        link
                        logo {
                            file {
                                url
                            }
                        }
                    }
                }
            }
        }

        banners: allContentfulBanner(
            filter: { page: { eq: "index" }, node_locale: { eq: $node_locale } }
        ) {
            edges {
                node {
                    link
                    banner {
                        file {
                            url
                        }
                    }
                }
            }
        }
    }
`;



// WEBPACK FOOTER //
// ./src/templates/indexTemplate.js